import base from "./base"
import { defineStore } from 'pinia'
import { backend_api_v2 } from "/src/utils/apiv2"

export const useLogisticsStore = defineStore('logistics', {
  state: () => ({
    materials: [],
    product_plates: [],
    product_profiles: [],
    plate_sizes: [],
    profile_sizes: [],
    racks: [],
    rack_traces: [],
    // Which tile of the delivery filter grid is active, "" for all. Not
    // persisted: it is a view of the list on screen, not a user setting.
    delivery_filter: "",
    // The rack load the delivery viewer currently has open, as
    // `{uuid, assemblies}`, or null. This is the scope of the scan-to-load
    // shortcut in the QR reader: with nothing open the reader behaves exactly
    // as it always has, so the shortcut can never surprise anyone who is not
    // standing at a rack.
    active_rack_load: null,
  }),
  getters: {
    // Rack setups offered in pickers: archived ones stay in the store so the
    // loads made with them can still resolve their geometry, and drop out of
    // the places where you pick one.
    activeRacks: state => state.racks.filter(r => !r.archived),
    rackByUuid: state => uuid => state.racks.find(r => r.uuid === uuid) || null,
    rackTraceByUuid: state => uuid => state.rack_traces.find(t => t.uuid === uuid) || null,
  },
  actions: {
    // Materials
    setMaterials: base.actions.setter("materials"),
    createMaterial: base.actions.creator("materials"),
    updateMaterial: base.actions.updater("materials"),
    removeMaterial: base.actions.remover("materials"),

    // Product Plates
    setProductPlates: base.actions.setter("product_plates"),
    createProductPlate: base.actions.creator("product_plates"),
    updateProductPlate: base.actions.updater("product_plates"),
    removeProductPlate: base.actions.remover("product_plates"),

    // Product Profiles
    setProductProfiles: base.actions.setter("product_profiles"),
    createProductProfile: base.actions.creator("product_profiles"),
    updateProductProfile: base.actions.updater("product_profiles"),
    removeProductProfile: base.actions.remover("product_profiles"),

    // Plate Sizes
    setPlateSizes: base.actions.setter("plate_sizes"),
    createPlateSize: base.actions.creator("plate_sizes"),
    updatePlateSize: base.actions.updater("plate_sizes"),
    removePlateSize: base.actions.remover("plate_sizes"),

    // Profile Sizes
    setProfileSizes: base.actions.setter("profile_sizes"),
    createProfileSize: base.actions.creator("profile_sizes"),
    updateProfileSize: base.actions.updater("profile_sizes"),
    removeProfileSize: base.actions.remover("profile_sizes"),

    // Racks
    setRacks: base.actions.setter("racks"),
    createRack: base.actions.creator("racks"),
    updateRack: base.actions.updater("racks"),
    removeRack: base.actions.remover("racks"),

    setActiveRackLoad(load) { this.active_rack_load = load },

    /** Tapping the active tile again clears the filter. */
    setDeliveryFilter(value) {
      this.delivery_filter = this.delivery_filter === value ? "" : value
    },

    // Rack Traces
    setRackTraces: base.actions.setter("rack_traces"),
    createRackTrace: base.actions.creator("rack_traces"),
    updateRackTrace: base.actions.updater("rack_traces"),
    removeRackTrace: base.actions.remover("rack_traces"),


    // Materials
    loadMaterials: base.actions.loader("logistics/materials/", "Materials"),
    postMaterial: base.actions.poster("logistics/materials/", "Material"),
    putMaterial: base.actions.putter("logistics/materials/", "Material"),
    deleteMaterial: base.actions.deleter("logistics/materials/", "Material"),

    // Product Plates
    loadProductPlates: base.actions.loader("logistics/products/plates/", "ProductPlates"),
    postProductPlate: base.actions.poster("logistics/products/plates/", "ProductPlate"),
    putProductPlate: base.actions.putter("logistics/products/plates/", "ProductPlate"),
    deleteProductPlate: base.actions.deleter("logistics/products/plates/", "ProductPlate"),

    // Product Profiles
    loadProductProfiles: base.actions.loader("logistics/products/profiles/", "ProductProfiles"),
    postProductProfile: base.actions.poster("logistics/products/profiles/", "ProductProfile"),
    putProductProfile: base.actions.putter("logistics/products/profiles/", "ProductProfile"),
    deleteProductProfile: base.actions.deleter("logistics/products/profiles/", "ProductProfile"),

    // Plate Sizes
    loadPlateSizes: base.actions.loader("logistics/sizes/plates/", "PlateSizes"),
    postPlateSize: base.actions.poster("logistics/sizes/plates/", "PlateSize"),
    putPlateSize: base.actions.putter("logistics/sizes/plates/", "PlateSize"),
    deletePlateSize: base.actions.deleter("logistics/sizes/plates/", "PlateSize"),

    // Profile Sizes
    loadProfileSizes: base.actions.loader("logistics/sizes/profiles/", "ProfileSizes"),
    postProfileSize: base.actions.poster("logistics/sizes/profiles/", "ProfileSize"),
    putProfileSize: base.actions.putter("logistics/sizes/profiles/", "ProfileSize"),
    deleteProfileSize: base.actions.deleter("logistics/sizes/profiles/", "ProfileSize"),

    // Rack setups
    loadRacks: base.actions.loader("logistics/delivery/racks/", "Racks"),
    postRack: base.actions.poster("logistics/delivery/racks/", "Rack"),
    putRack: base.actions.putter("logistics/delivery/racks/", "Rack"),
    deleteRack: base.actions.deleter("logistics/delivery/racks/", "Rack"),

    // Rack loads
    loadRackTraces: base.actions.loader_pr("logistics/delivery/rack_traces/", "RackTraces"),
    postRackTrace: base.actions.poster("logistics/delivery/rack_traces/", "RackTrace"),
    putRackTrace: base.actions.putter("logistics/delivery/rack_traces/", "RackTrace"),
    deleteRackTrace: base.actions.deleter("logistics/delivery/rack_traces/", "RackTrace"),
    refreshRackTrace: base.actions.refresher("logistics/delivery/rack_traces/", "RackTrace"),

    /**
     * Write the whole layout of a load in one request.
     *
     * Replaces the old loop of one PUT plus one GET per assembly, which took
     * 2N round-trips and left the load half-written when one of them failed.
     *
     * @param {string} uuid rack load uuid
     * @param {Array<{assembly: string, rack_side: string, rack_row: number, rack_row_pos: number}>} positions
     */
    async setRackTraceLayout(uuid, positions) {
      const { data } = await backend_api_v2.post(
        `logistics/delivery/rack_traces/${uuid}/set_layout/`, { positions })
      this.updateRackTrace(data)
      return data
    },

    /** Set `delivery_status` on every panel of a load at once. */
    async setRackTraceDeliveryStatus(uuid, delivery_status) {
      const { data } = await backend_api_v2.post(
        `logistics/delivery/rack_traces/${uuid}/set_delivery_status/`,
        { delivery_status })
      return data
    },

  },
})
